import React, { useState, useEffect } from "react";
import { Box, Snackbar, Alert } from "@mui/material";
import api from "../../utils/api";
import Sidebar from "./Sidebar";
import Toolbar from "./Toolbar";
import StorageStatus from "./StorageStatus";
import FolderGrid from "./FolderGrid";
import FileGrid from "./FileGrid";
import CreateFolderDialog from "./CreateFolderDialog";

const MAX_STORAGE = 5 * 1024 * 1024 * 1024; // 5GB

const FileManager = () => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState("Files");
  const [storageUsed, setStorageUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    fetchFilesAndFolders(currentPath);
  }, [currentPath]);

  const fetchFilesAndFolders = async (path) => {
    setLoading(true);
    try {
      const res = await api.get(`/file-manager?path=${path}`);
      setFolders(res.data.folders || []);
      setFiles(res.data.files || []);
      setStorageUsed(res.data.storageUsed || 0);
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to load files. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      await api.post(
        `/uploadfilesmanager?path=${encodeURIComponent(currentPath)}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      fetchFilesAndFolders(currentPath);
    } catch (err) {
      console.error("Upload error:", err);
      setErrorMsg("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName) return;
    try {
      await api.post(`/create-folder`, {
        folderPath: `${currentPath}/${newFolderName}`,
      });
      fetchFilesAndFolders(currentPath);
      setFolderDialogOpen(false);
      setNewFolderName("");
    } catch (err) {
      console.error("Folder create error:", err);
      setErrorMsg("Failed to create folder.");
    }
  };

  const handleRenameFile = async (oldName, newName) => {
    try {
      await api.put(`/rename-file`, {
        oldPath: `${currentPath}/${oldName}`,
        newPath: `${currentPath}/${newName}`,
      });
      fetchFilesAndFolders(currentPath);
    } catch (err) {
      console.error("Rename file error:", err);
      setErrorMsg("Failed to rename file.");
    }
  };

  const handleRenameFolder = async (oldName, newName) => {
    try {
      await api.put(`/rename-folder`, {
        oldPath: `${currentPath}/${oldName}`,
        newPath: `${currentPath}/${newName}`,
      });
      fetchFilesAndFolders(currentPath);
    } catch (err) {
      console.error("Rename folder error:", err);
      setErrorMsg("Failed to rename folder.");
    }
  };

  const handleDeleteFile = async (fileName) => {
    if (!window.confirm(`Delete ${fileName}?`)) return;
    try {
      await api.delete(`/deletefilesmanager`, {
        data: { filePath: `${currentPath}/${fileName}` },
      });
      fetchFilesAndFolders(currentPath);
    } catch (err) {
      console.error("Delete file error:", err);
      setErrorMsg("Failed to delete file.");
    }
  };

  const handleDeleteFolder = async (folderName) => {
    if (!window.confirm(`Delete folder ${folderName}?`)) return;
    try {
      await api.delete(`/delete-folder`, {
        data: { folderPath: `${currentPath}/${folderName}` },
      });
      fetchFilesAndFolders(currentPath);
    } catch (err) {
      console.error("Delete folder error:", err);
      setErrorMsg("Failed to delete folder.");
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <Sidebar
        folders={folders}
        onNavigate={(folder) => setCurrentPath(`${currentPath}/${folder}`)}
      />

      <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
        <Toolbar
          currentPath={currentPath}
          setCurrentPath={setCurrentPath}
          onUpload={handleUpload}
          onRefresh={() => fetchFilesAndFolders(currentPath)}
          onCreateFolder={() => setFolderDialogOpen(true)}
        />

        <Box sx={{ mt: 2.5, mb: 3 }}>
          <StorageStatus storageUsed={storageUsed} maxStorage={MAX_STORAGE} />
        </Box>

        <Box sx={{ mb: 4 }}>
          <FolderGrid
            folders={folders}
            onOpen={setCurrentPath}
            onDelete={handleDeleteFolder}
            onRename={handleRenameFolder}
            currentPath={currentPath}
          />
        </Box>

        <Box sx={{ mt: 1 }}>
          <FileGrid
            files={files}
            currentPath={currentPath}
            onDelete={handleDeleteFile}
            onRename={handleRenameFile}
          />
        </Box>

        <CreateFolderDialog
          open={folderDialogOpen}
          onClose={() => setFolderDialogOpen(false)}
          folderName={newFolderName}
          setFolderName={setNewFolderName}
          onCreate={handleCreateFolder}
        />
      </Box>

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={4000}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FileManager;
